"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "@/components/atsa/Link";
import { AppShell } from "@/components/atsa/AppShell";
import { clearAttempts, getRemotePercentile, loadAttempts, referencePercentile, summarize, type SectionSummary } from "@/lib/atsa/progress";
import { isSupabaseConfigured } from "@/lib/atsa/supabase";

function Sparkline({ scores }: { scores: number[] }) {
  if (scores.length < 2) return <div className="sparkline-empty">One attempt so far. A trend line appears after the next.</div>;
  const w = 240;
  const h = 48;
  const pad = 5;
  const points = scores
    .map((score, index) => {
      const x = pad + (index / (scores.length - 1)) * (w - 2 * pad);
      const y = pad + (1 - score / 100) * (h - 2 * pad);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg className="sparkline" viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="2" />
      {scores.map((score, index) => {
        const x = pad + (index / (scores.length - 1)) * (w - 2 * pad);
        const y = pad + (1 - score / 100) * (h - 2 * pad);
        return <circle key={index} cx={x} cy={y} r="2.2" fill="var(--accent)" />;
      })}
    </svg>
  );
}

function trendLabel(trend: number) {
  if (trend > 1) return { text: `▲ up ${trend}`, cls: "trend-up" };
  if (trend < -1) return { text: `▼ down ${Math.abs(trend)}`, cls: "trend-down" };
  return { text: "steady", cls: "trend-flat" };
}

export default function ProgressPage() {
  const [mounted, setMounted] = useState(false);
  const [showPercentile, setShowPercentile] = useState(false);
  const [version, setVersion] = useState(0);
  const [remote, setRemote] = useState<Record<string, number | null>>({});

  useEffect(() => setMounted(true), []);

  const summaries: SectionSummary[] = useMemo(
    () => (mounted ? summarize(loadAttempts()) : []),
    [mounted, version],
  );

  // When enabled and the database is configured, fetch a real cross-user
  // percentile per section. Sections without enough data return null and the
  // card falls back to the illustrative reference curve.
  useEffect(() => {
    if (!mounted || !showPercentile || !isSupabaseConfigured || summaries.length === 0) return;
    let active = true;
    (async () => {
      const entries = await Promise.all(
        summaries.map(async (s) => [s.sectionSlug, await getRemotePercentile(s.sectionSlug, s.recentAverage)] as const),
      );
      if (active) setRemote(Object.fromEntries(entries));
    })();
    return () => {
      active = false;
    };
  }, [mounted, showPercentile, summaries]);

  const totalAttempts = summaries.reduce((sum, s) => sum + s.count, 0);
  const weakest = [...summaries]
    .filter((s) => s.count >= 2)
    .sort((a, b) => a.recentAverage - b.recentAverage)
    .slice(0, 2);

  function handleClear() {
    if (typeof window !== "undefined" && window.confirm("Clear all saved progress? This cannot be undone.")) {
      clearAttempts();
      setVersion((v) => v + 1);
    }
  }

  return (
    <AppShell>
      <div className="content-page">
        <p className="eyebrow">PROGRESS</p>
        <h1>Your progress</h1>
        <p className="muted-lede">
          Every completed module is saved on this device and charted here so you can see whether your scores
          are moving. Nothing leaves your browser.
        </p>

        {!mounted ? (
          <p className="muted-lede">Loading…</p>
        ) : summaries.length === 0 ? (
          <div className="content-card">
            <p style={{ marginTop: 0 }}>No attempts saved yet. Finish any scored module and it will appear here.</p>
            <div className="button-row"><Link className="primary-button link-button" href="/atsa">Start practicing</Link></div>
          </div>
        ) : (
          <>
            <div className="progress-summary">
              <div className="progress-stat"><strong>{totalAttempts}</strong><span>attempts</span></div>
              <div className="progress-stat"><strong>{summaries.length}</strong><span>sections practiced</span></div>
            </div>

            {weakest.length > 0 ? (
              <div className="callout">
                Focus area: your lowest recent averages are {weakest.map((w) => `${w.section} (${w.recentAverage})`).join(" and ")}.
                A few targeted rounds there will move your overall standing fastest.
              </div>
            ) : null}

            <div className="percentile-toggle">
              <label className="check-row">
                <input type="checkbox" checked={showPercentile} onChange={(e) => setShowPercentile(e.target.checked)} />
                <span>{isSupabaseConfigured ? "Show percentile vs other users" : "Show reference percentile (illustrative)"}</span>
              </label>
              {showPercentile ? (
                <p className="percentile-note">
                  {isSupabaseConfigured
                    ? "Where enough attempts exist, this compares your recent average to other users of this site. Sections with too few attempts fall back to a fixed reference curve. The FAA does not publish official score distributions, so neither figure is an FAA percentile."
                    : "This places your recent average on a fixed reference curve so you can gauge yourself. It is not real candidate data. Connecting the database turns this into a real comparison against other users."}
                </p>
              ) : null}
            </div>

            <div className="progress-grid">
              {summaries.map((s) => {
                const t = trendLabel(s.trend);
                const real = remote[s.sectionSlug];
                const usingReal = real !== null && real !== undefined;
                const pct = showPercentile ? (usingReal ? real : referencePercentile(s.sectionSlug, s.recentAverage)) : null;
                return (
                  <div className="progress-card" key={s.sectionSlug}>
                    <div className="progress-card-head">
                      <h3>{s.section}</h3>
                      <span className={`trend-badge ${t.cls}`}>{t.text}</span>
                    </div>
                    <Sparkline scores={s.scores} />
                    <div className="progress-metrics">
                      <div><span>Recent</span><strong>{s.recent}</strong></div>
                      <div><span>Best</span><strong>{s.best}</strong></div>
                      <div><span>Avg of last 5</span><strong>{s.recentAverage}</strong></div>
                      <div><span>Attempts</span><strong>{s.count}</strong></div>
                    </div>
                    {pct !== null && pct !== undefined ? (
                      <p className="percentile-line">{usingReal ? "Vs other users" : "Reference"}: about the {pct}th percentile ({s.scoreLabel.toLowerCase()})</p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="button-row" style={{ marginTop: 24 }}>
              <Link className="primary-button link-button" href="/atsa">Keep practicing</Link>
              <button className="secondary-button" onClick={handleClear}>Clear progress</button>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
